import React, {
  Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import {
  Link,
  RouteComponentProps,
  useParams,
  useLocation,
  useHistory,
} from 'react-router-dom';
import {
  CircularProgress,
  Grid,
  Paper,
  MenuItem,
  Typography,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import ArrowLeftIcon from '@material-ui/icons/ArrowLeft';
import { useMutation, useQuery } from '@apollo/react-hooks';

import { useSnackbar } from 'notistack';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import useStyles from './styles';
import { transformGraphQlErrorMessage } from '../../utils/helpers';
import CustomButton from '../../components/CustomButton';
import CustomTextField from '../../components/CustomTextField';
import {
  HUB_ACCOUNT_BY_ID,
  UPDATE_HUB_ACCOUNT,
} from '../../graphql/queries/HubAccounts';
import { InputHubAccount } from '../../types/inputTypes/HubAccount';
import { goToZwaveTool } from '../../services/zwave-tool';
import BuilderSearch from '../../components/BulderSearch/BuilderSearch';
import Builder from '../../types/Builder';

import {
  getSchlageAuthUrl,
  HUB_ACCOUNT_TYPE,
  isManagedSchlageAccountType,
  isSchlageAccountType,
} from './constant';

type Props = {
  loading?: boolean;
} & RouteComponentProps;

const HubAccountForm: React.FC<Props> = () => {
  const classes = useStyles();
  const { id: hubAccountId } = useParams<{ id: string }>();
  const isCreateMode = !hubAccountId;

  const [hubAccount, setHubAccount] = useState<InputHubAccount | null>(() => (
    isCreateMode ? new InputHubAccount() : null
  ));
  const location = useLocation();
  const historyApi = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  // Read the Schlage OAuth redirect token once at init
  const [pendingSchlageToken] = useState<string | null>(
    () => new URLSearchParams(location.search).get('token'),
  );

  const handleHubAccountChange = useCallback(
    ({ target: { name, value } }: React.ChangeEvent<HTMLInputElement>) => {
      if (name) {
        setHubAccount((prevHubAccount: InputHubAccount | null) => {
          const base = prevHubAccount ?? new InputHubAccount();
          return {
            ...base,
            [name]: value,
          };
        });
      }
    },
    [],
  );

  const { data, loading, error } = useQuery<{ getHubAccountById: InputHubAccount }>(
    HUB_ACCOUNT_BY_ID,
    {
      variables: { id: hubAccountId },
      fetchPolicy: 'network-only',
      skip: !hubAccountId,
    },
  );

  const [updateHubAccount] = useMutation<{ updateHubAccount: InputHubAccount }>(
    UPDATE_HUB_ACCOUNT,
    { errorPolicy: 'all' },
  );

  const prevHubAccountIdRef = useRef<string | undefined>(hubAccountId);
  const schlageTokenSavedRef = useRef(false);

  // Strip the OAuth redirect token from the URL on mount
  useEffect(() => {
    if (pendingSchlageToken) {
      historyApi.replace(location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-persist the Schlage token as soon as account data is available.
  useEffect(() => {
    if (!pendingSchlageToken || schlageTokenSavedRef.current) return;
    if (!data?.getHubAccountById) return;

    schlageTokenSavedRef.current = true;
    updateHubAccount({
      variables: {
        hubAccount: {
          ...data.getHubAccountById,
          schlage_refresh_token: pendingSchlageToken,
        },
      },
    })
      .then(() => {
        enqueueSnackbar('Schlage account pairing completed', { variant: 'success' });
      })
      .catch(err => {
        enqueueSnackbar(transformGraphQlErrorMessage(err.message), { variant: 'error' });
      });
  }, [data, pendingSchlageToken, updateHubAccount, enqueueSnackbar]);

  const handleHubAccountSave = () => {
    if (!hubAccount) return;
    try {
      updateHubAccount({
        variables: {
          hubAccount,
        },
      })
        .then(() => historyApi.push('/accounts'))
        .catch(err => {
          enqueueSnackbar(transformGraphQlErrorMessage(err.message), {
            variant: 'error',
          });
        });
    } catch (errors) {
      enqueueSnackbar('Error', { variant: 'error' });
    }
  };

  useLayoutEffect(() => {
    const prevRouteId = prevHubAccountIdRef.current;

    if (!hubAccountId) {
      if (prevRouteId) {
        setHubAccount(new InputHubAccount());
      }
      prevHubAccountIdRef.current = undefined;
      return;
    }

    prevHubAccountIdRef.current = hubAccountId;

    if (loading || !data?.getHubAccountById) {
      setHubAccount(null);
      return;
    }

    const fromServer = data.getHubAccountById;
    setHubAccount({
      ...fromServer,
      schlage_refresh_token:
        pendingSchlageToken
        ?? fromServer.schlage_refresh_token
        ?? '',
    });
  }, [hubAccountId, loading, data, pendingSchlageToken]);

  const handleControlHubs = () => {
    goToZwaveTool(hubAccountId);
  };

  const isSchlageAccount = isSchlageAccountType(hubAccount?.account_type);
  const isManagedSchlageAccount = isManagedSchlageAccountType(hubAccount?.account_type);

  const handleSchlagePairing = async () => {
    const baseUrl = process.env.REACT_APP_WEBSOCKET_SERVER_URL;
    if (!baseUrl) return;

    let accountId: string | undefined = hubAccountId;

    if (!accountId) {
      if (!hubAccount) return;
      try {
        const result = await updateHubAccount({ variables: { hubAccount } });
        accountId = result?.data?.updateHubAccount?.id;
        if (!accountId) return;
        historyApi.replace(`/accounts/${accountId}`);
      } catch (err) {
        enqueueSnackbar(transformGraphQlErrorMessage((err as any).message), { variant: 'error' });
        return;
      }
    }

    const returnUrl = `${window.location.origin}/accounts/${accountId}`;
    window.open(
      getSchlageAuthUrl(baseUrl, returnUrl, hubAccount?.account_type),
      '_self',
    );
  };

  const handleBuilderChange = (builder: Builder) => {
    setHubAccount((prevHubAccount: InputHubAccount | null) => {
      const base = prevHubAccount ?? new InputHubAccount();
      return {
        ...base,
        builder_id: builder.builder_id,
      };
    });
  };

  const showEditLoader = Boolean(hubAccountId) && (loading || !hubAccount);
  const showEditError = Boolean(hubAccountId) && !loading && Boolean(error);

  return (
    <Fragment>
      <div className={classes.actionsBar}>
        <div className={classes.actionButtonsWrapper}>
          <Button
            variant="contained"
            className={classes.backToButton}
            component={Link}
            to="/accounts"
          >
            <ArrowLeftIcon />
            Hub Accounts
          </Button>
        </div>
      </div>
      <div className={classes.contentWrapper}>
        <Grid item xs={12} sm={12}>
          <Paper className={classes.paper}>
            {showEditError && (
              <Typography color="error" style={{ padding: 16 }}>
                {transformGraphQlErrorMessage(error!.message)}
              </Typography>
            )}
            {showEditLoader && !showEditError && (
              <Grid
                container
                justify="center"
                alignItems="center"
                style={{ minHeight: 280 }}
              >
                <CircularProgress />
              </Grid>
            )}
            {!showEditLoader && hubAccount && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  label="Account type"
                  value={hubAccount.account_type}
                  name="account_type"
                  select
                  fullWidth
                  onChange={handleHubAccountChange}
                >
                  {HUB_ACCOUNT_TYPE.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </CustomTextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  fullWidth
                  name="email"
                  label="Email"
                  value={hubAccount.email}
                  onChange={handleHubAccountChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  fullWidth
                  name="username"
                  label="Username"
                  value={hubAccount.username}
                  onChange={handleHubAccountChange}
                />
              </Grid>
              {!isSchlageAccount && (
                <>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      fullWidth
                      name="hashpw"
                      label="Password"
                      value={hubAccount.hashpw}
                      onChange={handleHubAccountChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      fullWidth
                      name="adc_group_id"
                      label="ADC Group Id"
                      value={hubAccount.adc_group_id}
                      onChange={handleHubAccountChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      fullWidth
                      name="smart_lock_connected_account_id"
                      label="Smart Lock Connected Account Id"
                      value={hubAccount.smart_lock_connected_account_id}
                      onChange={handleHubAccountChange}
                    />
                  </Grid>
                </>
              )}
              {isSchlageAccount && (
                <>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      fullWidth
                      name="schlage_refresh_token"
                      label="Schlage Refresh Token"
                      value={hubAccount.schlage_refresh_token ?? ''}
                      onChange={handleHubAccountChange}
                      disabled={isManagedSchlageAccount}
                      helperText={isManagedSchlageAccount ? 'Set by Schlage pairing' : undefined}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <CustomButton
                      type="button"
                      variant="blue"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSchlagePairing();
                      }}
                    >
                      Schlage pairing
                    </CustomButton>
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6}>
                <BuilderSearch
                  margin="normal"
                  onBuilderChange={handleBuilderChange}
                  label="Builder"
                  initialValue={hubAccount.builder_id ?? 0}
                />
              </Grid>
              <Grid
                container
                spacing={4}
                justify="center"
                style={{ marginTop: '10px', marginBottom: '10px' }}
              >
                <Grid item>
                  <CustomButton variant="orange" onClick={handleHubAccountSave}>
                    {hubAccountId ? 'Save' : 'Add'}
                  </CustomButton>
                </Grid>
                <Grid item>
                  <CustomButton variant="orange" onClick={() => handleControlHubs()}>
                    <DeviceHubIcon />
                    {' '}
                    Control Hubs
                  </CustomButton>
                </Grid>
              </Grid>
            </Grid>
            )}
          </Paper>
        </Grid>
      </div>
    </Fragment>
  );
};

export default React.memo(HubAccountForm);
